import React, { createRef } from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { ITextarea } from 'components/Form/Textarea/Textarea';
import PVQName from 'components/Form/PVQName';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { foreclosureEntityTypes, foreclosurePropertyTypes } from 'constants/enums/financialOptions';
import { FinancialForeclosureItemModel } from 'forms/sections/financial/foreclosure/types';
import NotApplicable from 'components/Form/NotApplicable';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { DeepSafePartial } from 'types/types';

interface Props extends DeepSafePartial<FinancialForeclosureItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class ForeclosureItem extends ValidationElement<Props> {
  actionsTakenRef = createRef<ITextarea>();

  update = (queue: DeepSafePartial<FinancialForeclosureItemModel>) => {
    this.props.onUpdate({
      EntityType: this.props.EntityType,
      EntityTypeExplain: this.props.EntityTypeExplain,
      EntityName: this.props.EntityName,
      PrivateName: this.props.PrivateName,
      PropertyType: this.props.PropertyType,
      PropertyTypeExplain: this.props.PropertyTypeExplain,
      PropertyLocation: this.props.PropertyLocation,
      FirstPastDue: this.props.FirstPastDue,
      NumberMissed: this.props.NumberMissed,
      NumberMissedEstimated: this.props.NumberMissedEstimated,
      AmountOwed: this.props.AmountOwed,
      AmountOwedEstimated: this.props.AmountOwedEstimated,
      Why: this.props.Why,
      When: this.props.When,
      Current: this.props.Current,
      WhenResolved: this.props.WhenResolved,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      CourtName: this.props.CourtName,
      CourtNameNotApplicable: this.props.CourtNameNotApplicable,
      CourtLocation: this.props.CourtLocation,
      CourtLocationNotApplicable: this.props.CourtLocationNotApplicable,
      OrderNum: this.props.OrderNum,
      OrderNumIdk: this.props.OrderNumIdk,
      ...queue,
    });
  };

  updateEntityType = (values: FinancialForeclosureItemModel['EntityType']) => {
    this.update({
      EntityType: values,
    });
  };

  updateEntityTypeExplain = (values: FinancialForeclosureItemModel['EntityTypeExplain']) => {
    this.update({
      EntityTypeExplain: values,
    });
  };

  updateEntityName = (values: FinancialForeclosureItemModel['EntityName']) => {
    this.update({
      EntityName: values,
    });
  };

  updatePrivateName = (values: FinancialForeclosureItemModel['PrivateName']) => {
    this.update({
      PrivateName: values,
    });
  };

  updatePropertyType = (values: FinancialForeclosureItemModel['PropertyType']) => {
    this.update({
      PropertyType: values,
    });
  };

  updatePropertyTypeExplain = (values: FinancialForeclosureItemModel['PropertyTypeExplain']) => {
    this.update({
      PropertyTypeExplain: values,
    });
  };

  updatePropertyLocation = (values: FinancialForeclosureItemModel['PropertyLocation']) => {
    this.update({
      PropertyLocation: values,
    });
  };

  updateFirstPastDue = (values: FinancialForeclosureItemModel['FirstPastDue']) => {
    this.update({
      FirstPastDue: values,
    });
  };

  updateNumberMissed = (values: FinancialForeclosureItemModel['NumberMissed']) => {
    this.update({
      NumberMissed: values,
    });
  };

  updateAmountOwed = (values: FinancialForeclosureItemModel['AmountOwed']) => {
    this.update({
      AmountOwed: values,
    });
  };

  updateWhy = (values: FinancialForeclosureItemModel['Why']) => {
    this.update({
      Why: values,
    });
  };

  updateWhen = (values: FinancialForeclosureItemModel['When']) => {
    this.update({
      When: values,
    });
  };

  updateCurrent = (values: FinancialForeclosureItemModel['Current']) => {
    this.update({
      Current: values,
    });
  };

  updateWhenResolved = (values: FinancialForeclosureItemModel['WhenResolved']) => {
    this.update({
      WhenResolved: values,
    });
  };

  updateCourtName = (values: FinancialForeclosureItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtNameNotApplicable = (values: FinancialForeclosureItemModel['CourtNameNotApplicable']) => {
    this.update({
      CourtNameNotApplicable: values,
      CourtName: values?.applicable ? this.props.CourtName : {} as FinancialForeclosureItemModel['CourtName'],
    });
  };

  updateCourtLocation = (values: FinancialForeclosureItemModel['CourtLocation']) => {
    this.update({
      CourtLocation: values,
    });
  };

  updateCourtLocationNotApplicable = (values: FinancialForeclosureItemModel['CourtNameNotApplicable']) => {
    this.update({
      CourtLocationNotApplicable: values,
      CourtLocation: values?.applicable ? this.props.CourtLocation : {},
    });
  };

  updateOrderNum = (values: FinancialForeclosureItemModel['OrderNum']) => {
    this.update({
      OrderNum: values,
    });
  };

  updateOrderNumIdk = (values: FinancialForeclosureItemModel['OrderNumIdk']) => {
    this.update({
      OrderNumIdk: values,
      OrderNum: values?.applicable ? this.props.OrderNum : {} as FinancialForeclosureItemModel['OrderNum'],
    });
  };

  updateNumberMissedEstimated = (values: FinancialForeclosureItemModel['NumberMissedEstimated']) => {
    this.update({
      NumberMissedEstimated: values,
    });
  };

  updateAmountOwedEstimated = (values: FinancialForeclosureItemModel['AmountOwedEstimated']) => {
    this.update({
      AmountOwedEstimated: values,
    });
  };

  updateActionsTaken = (values: FinancialForeclosureItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenNone = (values: FinancialForeclosureItemModel['ActionsTakenNone']) => {
    if (this.actionsTakenRef?.current) { this.actionsTakenRef.current.clearValue(); }
    this.update({
      ActionsTakenNone: values,
      ActionsTaken: values?.applicable !== false ? this.props.ActionsTaken : {} as FinancialForeclosureItemModel['ActionsTaken'],
      ActionsTakenReason: values?.applicable === false ? this.props.ActionsTakenReason : {} as FinancialForeclosureItemModel['ActionsTakenReason'],
    });
  };

  updateActionsTakenReason = (values: FinancialForeclosureItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="foreclosure-item">
        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.whoRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.EntityType`}
        >
          <RadioGroup
            {...this.props.EntityType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.EntityType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.EntityTypeRadioGroup`}
          >
            {foreclosureEntityTypes.map((entityType) => (
              <Radio
                key={entityType}
                label={entityType}
                value={entityType}
                onUpdate={this.updateEntityType}
              />
            ))}
          </RadioGroup>
        </Field>

        {(this.props.EntityType?.value === 'Company' || this.props.EntityType?.value === 'Government Agency') && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.whoRadioGroup.isPublicOrGov')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.EntityName`}
          >
            <Text
              {...this.props.EntityName}
              attribute={`${attribute}.EntityName`}
              name="entityname"
              onUpdate={this.updateEntityName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}
        {this.props.EntityType?.value === 'Individual' && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.whoRadioGroup.isPrivateName')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PrivateName`}
          >
            <PVQName
              {...this.props.PrivateName}
              attribute={`${attribute}.PrivateName`}
              name="entityname"
              onUpdate={this.updatePrivateName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showLastFirstMiddleNameOrder
            />
          </Field>
        )}
        {this.props.EntityType?.value === 'Other' && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.whoRadioGroup.isOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.EntityTypeExplain`}
          >
            <Textarea
              {...this.props.EntityTypeExplain}
              attribute={`${attribute}.EntityTypeExplain`}
              name="entitytypeexplain"
              onUpdate={this.updateEntityTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.propertyTypeRadioGroup.title')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyType`}
        >
          <RadioGroup
            {...this.props.PropertyType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.PropertyType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.PropertyTypeRadioGroup`}
          >
            {foreclosurePropertyTypes.map((propertyType) => (
              <Radio
                key={propertyType}
                label={propertyType}
                value={propertyType}
                onUpdate={this.updatePropertyType}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.PropertyType?.value === 'Other' && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.propertyTypeRadioGroup.ifOther')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PropertyTypeExplain`}
          >
            <Textarea
              {...this.props.PropertyTypeExplain}
              attribute={`${attribute}.PropertyTypeExplain`}
              name="propertytypeexplain"
              onUpdate={this.updatePropertyTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.propertyLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyLocation`}
        >
          <LocationUnknown
            name="PropertyLocation"
            attribute={`${attribute}.PropertyLocation`}
            {...this.props.PropertyLocation}
            country="United States"
            onUpdate={this.updatePropertyLocation}
            onError={this.props.onError}
            layout={LocationUnknown.US_ADDRESS}
            geocode
            dispatch={this.props.dispatch}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.missedPayments.whenFirstDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.FirstPastDue`}
        >
          <DateControl
            {...this.props.FirstPastDue}
            attribute={`${attribute}.FirstPastDue`}
            name="firstpastdue"
            onUpdate={this.updateFirstPastDue}
            hideDay
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.missedPayments.numberMissedText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.NumberMissed`}
        >
          <Text
            {...this.props.NumberMissed}
            attribute={`${attribute}.NumberMissed`}
            name="nummissed"
            onUpdate={this.updateNumberMissed}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          <Checkbox
            label={i18n.t('financial.pvq.shared.estimated')}
            {...this.props.NumberMissedEstimated}
            onUpdate={this.updateNumberMissedEstimated}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.NumberMissed`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.amountOwedText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountOwed`}
        >
          <Currency
            maxLength="10"
            {...this.props.AmountOwed}
            attribute={`${attribute}.AmountOwed`}
            name="amountowed"
            onUpdate={this.updateAmountOwed}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          <Checkbox
            label={i18n.t('financial.pvq.shared.estimated')}
            {...this.props.AmountOwedEstimated}
            onUpdate={this.updateAmountOwedEstimated}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.AmountOwed`}
          />
        </Field>
        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.whyStoppedTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Why`}
        >
          <Textarea
            {...this.props.Why}
            attribute={`${attribute}.Why`}
            name="whystopped"
            onUpdate={this.updateWhy}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.When`}
        >
          <DateControl
            {...this.props.When}
            attribute={`${attribute}.When`}
            name="when"
            onUpdate={this.updateWhen}
            hideDay
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated
          />
        </Field>

        <Branch
          {...this.props.Current}
          name="current"
          label={i18n.t('financial.pvq.foreclosure.branch.ifYes.branch.title')}
          labelSize="h4"
          className="foreclosure-current-branch"
          onUpdate={this.updateCurrent}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.Current`}
        />

        {this.props.Current?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.branch.ifNo.whenClosedDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenResolved`}
          >
            <DateControl
              {...this.props.WhenResolved}
              attribute={`${attribute}.WhenResolved`}
              name="whenresolved"
              onUpdate={this.updateWhenResolved}
              hideDay
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated
            />
          </Field>
        )}

        {this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.branch.ifYes.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
            exactAttribute
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTaken`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
              exactAttribute
            >
              <Textarea
                {...this.props.ActionsTaken}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
                ref={this.actionsTakenRef}
                exactAttribute
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.foreclosure.branch.ifYes.branch.ifYes.combobox.ifNone')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTakenReason`}
            exactAttribute
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTakenReason`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
              exactAttribute
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.courtNameTextNotApplicable')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <NotApplicable
            {...this.props.CourtNameNotApplicable}
            label={i18n.t('financial.pvq.shared.na')}
            onUpdate={this.updateCourtNameNotApplicable}
            attribute={`${attribute}.CourtNameNotApplicable`}
          >
            <Text
              {...this.props.CourtName}
              attribute={`${attribute}.CourtName`}
              name="courtname"
              onUpdate={this.updateCourtName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.courtLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtLocation`}
        >
          {/*  In over 20 states there is non-judicial foreclosures, meaning no court is involved */}
          <NotApplicable
            {...this.props.CourtLocationNotApplicable}
            label={`Not Applicable/I don't know`}
            onUpdate={this.updateCourtLocationNotApplicable}
            attribute={`${attribute}.CourtLocationNotApplicable`}
          >
            <LocationUnknown
              name="CourtLocation"
              attribute={`${attribute}.CourtLocation`}
              {...this.props.CourtLocation}
              country="United States"
              onUpdate={this.updateCourtLocation}
              onError={this.props.onError}
              layout={LocationUnknown.US_ADDRESS}
              geocode
              dispatch={this.props.dispatch}
              required={this.props.required}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.foreclosure.branch.ifYes.orderNumTextIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderNum`}
        >
          <NotApplicable
            {...this.props.OrderNumIdk}
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateOrderNumIdk}
            attribute={`${attribute}.OrderNumIdk`}
          >
            <Text
              {...this.props.OrderNum}
              attribute={`${attribute}.OrderNum`}
              name="ordernum"
              onUpdate={this.updateOrderNum}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>
      </div>
    );
  }
}
ForeclosureItem.defaultProps = {
  OrderNumIdk: { applicable: true },
  CourtNameNotApplicable: { applicable: true },
  CourtLocationNotApplicable: { applicable: true },
  onUpdate: (queue) => { },
  onError: (value, arr) => arr,
  required: false,
};
